import { useLoadPasscodeListMembers } from '../../hooks/use-load-passcode-list-members';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectAssociatedEvents,
  selectAssociatedEventsDetails,
  selectPasscodeList,
} from '@/store/slices/passcode-lists/selectors';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { NavLink, useParams } from 'react-router-dom';
import {
  fetchPasscodeListAssociatedEvents,
  fetchPasscodeListAssociatedEventsDetails,
} from '@/store/slices/passcode-lists';
import { DownloadCSVStringValues, PASSCODE_LIST_TYPE } from '@/types/registration.types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import OptionalComponent from '@ui/optional-component/optional-component';
import PasscodeListMemberList from '../passcode-lists/passcode-list-member-table';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import PasscodeListAttachedProjects from '../passcode-lists/passcode-list-attached-projects';
import styles from './passcode-lists.module.css';
import classNames from 'classnames';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import AudiencesAddButton from '../../controls/audiences-add-button';
import SearchInput from '@ui/search-input/search-input';
import ManageRegistrantsModal from '../registrants/modals/add-registrants-modal';
import { useSaveRegistrantList } from '../../hooks/use-save-registrant-list';
import { HandleDownloadCSV } from '@/connection/audiences/passcode-lists';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';
import { downloadCsv, getDisplayName, getExportDate } from '../../utils';
import { GenerateMagicLinks, SendMagicLinkEmails } from '@/connection/audiences/send-magic-links';
import { selectPublishedStatus, selectWorkingProject } from '@/store/slices/project/selectors';
import { eventEnd } from '@/utils/event-end';
import Button from '@ui/buttons/button/button';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';

const AudiencesList = () => {
  const channel = useAppSelector(getActiveChannel);
  const associatedEvents = useAppSelector(selectAssociatedEvents);
  const associatedEventsDetails = useAppSelector(selectAssociatedEventsDetails);
  const _passcodeList = useAppSelector(selectPasscodeList);
  const workingProject = useAppSelector(selectWorkingProject);
  const publishStatus = useAppSelector(selectPublishedStatus);

  const isPublished = !!publishStatus?.url;

  const ended = useMemo(() => {
    // this does't need to work like a clock, it just needs to determine whether the event
    // is past any reasonable registration time for sending the magic links. Nobody
    // will be camping on the audiences list staring at it waiting for the event time to end.
    return Date.now() > eventEnd(workingProject);
  }, [workingProject]);

  const [showAddMembersModal, setShowAddMembersModal] = useState(false);

  const { projectUuid, language, passcodeListId } = useParams();
  const [sendingLinks, setSendingLinks] = useState(false);

  // ensure that this does not attempt to render the wrong list
  const passcodeList = useMemo(() => {
    if (Number(passcodeListId) === _passcodeList?.passcode_list) {
      return _passcodeList;
    }
  }, [passcodeListId, _passcodeList]);

  const { reloadMembers } = useLoadPasscodeListMembers(
    channel || 0,
    Number(passcodeListId) || 0,
    undefined,
    undefined,
    undefined,
    false
  );

  const handleSaveComplete = () => {
    reloadMembers();
  };

  const {
    saveEmailsOnlyList,
    savePasscodesOnlyList,
    saveEmailsAndPasscodesList,
    saveMagicLinksList,
    saveMagicLinksRegistrationList,
    saving,
  } = useSaveRegistrantList();

  const dispatch = useAppDispatch();

  const getSaveHandler = useCallback(() => {
    if (!passcodeList || passcodeList.type == null) return saveMagicLinksRegistrationList;

    const listType = passcodeList.type as PASSCODE_LIST_TYPE;

    switch (listType) {
      case PASSCODE_LIST_TYPE.EMAILS_ONLY:
        return saveEmailsOnlyList;
      case PASSCODE_LIST_TYPE.PASSCODES_ONLY:
        return savePasscodesOnlyList;
      case PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES:
        return saveEmailsAndPasscodesList;
      case PASSCODE_LIST_TYPE.MAGIC_LINKS:
        return saveMagicLinksList;
      case PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION:
        return saveMagicLinksRegistrationList;
      default:
        return saveMagicLinksRegistrationList;
    }
  }, [
    passcodeList,
    saveEmailsAndPasscodesList,
    saveEmailsOnlyList,
    saveMagicLinksList,
    saveMagicLinksRegistrationList,
    savePasscodesOnlyList,
  ]);

  const handlePasscodeListDownload = useCallback(async () => {
    try {
      if (passcodeList) {
        const response = await HandleDownloadCSV(
          passcodeList,
          DownloadCSVStringValues.NORMAL,
          undefined,
          null,
          passcodeList.dma_id
        );

        if (!response.ok) {
          toastTrigger('There was an error downloading the information', {
            type: EToastTypes.error,
            autoClose: 10000,
          });
          return;
        }

        downloadCsv(response, `${passcodeList.name ? passcodeList.name : 'passcode list export'}-${getExportDate()}`);
      }
    } catch (e) {
      console.error('Error downloading magic links:', e);
      toastTrigger('Download failed', {
        description: 'Your audience list failed to generate',
        type: EToastTypes.error,
        autoClose: 10000,
      });
    }
  }, [passcodeList]);

  const handleSendMagicLink = useCallback(async () => {
    try {
      if (!projectUuid || isNaN(Number(passcodeListId)) || !channel) return;

      setSendingLinks(true);
      // must be done in-order
      await GenerateMagicLinks(channel, Number(passcodeListId), projectUuid);
      await SendMagicLinkEmails(projectUuid, Number(passcodeListId));
      toastTrigger('Sending', { description: 'Magic links are sending' });
    } catch (e) {
      console.error(e);
      toastTrigger('Error sending magic links', { type: 'error' });
    } finally {
      setSendingLinks(false);
    }
  }, [passcodeListId, projectUuid, channel]);

  useEffect(() => {
    if (passcodeList?.passcode_list && channel) {
      dispatch(fetchPasscodeListAssociatedEvents({ passcodeListId: passcodeList.passcode_list, channel }));
    }
  }, [dispatch, passcodeList, channel]);

  useEffect(() => {
    if (associatedEvents && channel) {
      dispatch(fetchPasscodeListAssociatedEventsDetails({ eventList: associatedEvents, channel }));
    }
  }, [dispatch, associatedEvents, channel]);

  const tooltipText = useMemo(() => {
    if (!isPublished) {
      return 'Project must be published';
    }

    if (ended) {
      return 'Project is ended';
    }

    if (!passcodeList?.member_count) {
      return 'No members';
    }

    return (
      <>
        To send magic links the project
        <br />
        must be published and not ended.
      </>
    );
  }, [isPublished, ended, passcodeList?.member_count]);

  return (
    <>
      <Container className="flex-column gap-6">
        <Container
          className="flex ai-center"
          skLoader={{
            height: 40,
            width: 300,
            radius: 4,
            loading: !passcodeList,
            design: 'GlobalCont1',
            justify: 'flex-start',
          }}
        >
          <NavLink
            className={classNames(styles['passcode-list-back-button'], 'flex ai-center padding-r-8')}
            to={`/project/edit/${projectUuid}/${language}/audiences/lists`}
          >
            <Icon name={ICONS.ARROW_LEFT} size={20} />
          </NavLink>
          <Text tag="h2">{passcodeList?.name ? getDisplayName(passcodeList?.name) : 'Audience'}</Text>
        </Container>
        <Container className="flex ai-center padding-b-6" style={{ borderBottom: '1px solid var(--gray-600)' }}>
          <PasscodeListAttachedProjects
            displayAssociatedEvents
            passcodeListAssociatedEvents={associatedEventsDetails}
            passcodeListDownload={handlePasscodeListDownload}
          />
          <Container className="flex ai-center gap-6" style={{ marginLeft: 'auto' }}>
            <SearchInput expandOnFocus width="300px" trackInParams debounceTime={500} isClearable isBorderless />
            <TooltipPortal
              showOnFocus
              showOnHover={true}
              alwaysCloseOnMouseOut
              closeOnEscape
              delayStart={600}
              tooltipContent={
                <Text className="C1 text-center" color="var(--gray-true-dark-100)">
                  {tooltipText}
                </Text>
              }
              animationType="slide-up"
              styles={{ padding: '0 4px' }}
              tailSize={0}
              verticalOffset={-6}
            >
              <Button
                onClick={handleSendMagicLink}
                design="LocalBttnSec"
                round
                designSize="small"
                disabled={!isPublished || ended || !passcodeList?.member_count}
                loading={sendingLinks}
              >
                <Icon name={ICONS.MAGIC_LINK_SPARKLE} size={12} />
              </Button>
            </TooltipPortal>
            <AudiencesAddButton onAddClick={() => setShowAddMembersModal(true)} />
          </Container>
        </Container>
      </Container>
      <OptionalComponent display={!!passcodeList}>
        {passcodeList ? <PasscodeListMemberList passcodeList={passcodeList} reloadMembers={reloadMembers} /> : <></>}
      </OptionalComponent>

      <ManageRegistrantsModal
        open={showAddMembersModal}
        setOpen={setShowAddMembersModal}
        onSave={getSaveHandler()}
        saving={saving}
        passcodeList={passcodeList || undefined}
        onSaveComplete={handleSaveComplete}
      />
    </>
  );
};

export default AudiencesList;
